import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@client/lib';

import { AddPolicyModal } from '@client/components/AddPolicyModal';
import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  getShellExpandKey,
  useAccountAccordion,
} from '@/features/guided-financial-planning/hooks/use-account-accordion';
import { usePartnerLockedFundingSources } from '@/features/guided-financial-planning/hooks/use-partner-locked-funding-sources';
import { usePartnerAllocation, DEFAULT_CUSTOM_CLIENT_PERCENT } from '@/features/guided-financial-planning/hooks/use-partner-allocation';
import { AccountCard, type AllocationCallbacks } from './account-card';
import { ExistingLtcPolicyCard } from './existing-ltc-policy-card';
import type { AssetTypeId } from '@/features/guided-financial-planning/config';

interface AccountAccordionListProps {
  className?: string;
  onAllCompleteChange?: (allComplete: boolean) => void;
  disabled?: boolean;
}

export function AccountAccordionList({
  className,
  onAllCompleteChange,
  disabled,
}: AccountAccordionListProps) {
  const {
    partnerSharedFundingSourceIds,
    partnerFirstName,
    isLoading: isLoadingPartnerData,
  } = usePartnerLockedFundingSources();
  const { accountShells, allComplete, expandedShellKey, handleToggle, handleSaveComplete } =
    useAccountAccordion({ partnerSharedFundingSourceIds });
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const { hasPartner, client, partnerClient, getCardAllocationState, applyCardAllocation } =
    usePartnerAllocation();
  const [existingLtcDrawerOpen, setExistingLtcDrawerOpen] = useState(false);

  const existingLtcShell = useMemo(
    () => accountShells.find(s => s.isExistingLtcPolicy),
    [accountShells],
  );
  const policyFundingSourceIdForDrawer = existingLtcShell?.policyFundingSourceId ?? undefined;

  // Build allocation lookup from snapshot for completed sessions
  const allocationsBySourceId = useMemo(() => {
    if (!isSessionComplete) return {};
    const allocations = session?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
    return allocations.reduce(
      (acc, allocation) => {
        acc[allocation.selfFundingSourceId] = allocation.amount;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [isSessionComplete, session?.stepData?.planComparisonSnapshot?.fundingAllocations]);

  const clientName = client?.clientFirstName ?? 'You';
  const partnerName = partnerClient?.clientFirstName ?? 'Partner';

  const buildAllocationCallbacks = useCallback(
    (
      fundingSourceId: string | null,
      assetTypeId: AssetTypeId,
      selfFundingSource?: SelfFundingSource,
    ): AllocationCallbacks | undefined => {
      if (!hasPartner) return undefined;
      return {
        hasPartner: true,
        clientName,
        partnerName,
        initialState: fundingSourceId
          ? getCardAllocationState(fundingSourceId)
          : { enabled: false, option: 'mirror' as const, customClientPercent: DEFAULT_CUSTOM_CLIENT_PERCENT },
        onApply: (state, overrides) =>
          applyCardAllocation(
            overrides?.fundingSourceId ?? fundingSourceId ?? '',
            assetTypeId,
            overrides?.sourceData ?? selfFundingSource!,
            state,
          ),
      };
    },
    [hasPartner, clientName, partnerName, getCardAllocationState, applyCardAllocation],
  );

  useEffect(() => {
    onAllCompleteChange?.(allComplete);
  }, [allComplete, onAllCompleteChange]);

  const isShellLocked = useCallback(
    (shell: (typeof accountShells)[number]) =>
      !!shell.fundingSourceId && partnerSharedFundingSourceIds.has(shell.fundingSourceId),
    [partnerSharedFundingSourceIds],
  );

  const regularShells = useMemo(
    () => accountShells.filter(s => !isShellLocked(s)),
    [accountShells, isShellLocked],
  );
  const lockedShells = useMemo(
    () => accountShells.filter(s => !s.isExistingLtcPolicy && isShellLocked(s)),
    [accountShells, isShellLocked],
  );

  if (isLoadingPartnerData) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('space-y-4', className)}>
        {regularShells.map(shell =>
          shell.isExistingLtcPolicy ? (
            <ExistingLtcPolicyCard
              key="existing-ltc-policy"
              label={shell.label}
              hasPolicy={Boolean(shell.policyFundingSourceId)}
              policyFaceAmount={shell.policyMaximumBenefitAmount ?? undefined}
              policyTypeName={shell.policyTypeName ?? undefined}
              disabled={isSessionComplete ? false : disabled}
              onClick={() => setExistingLtcDrawerOpen(true)}
            />
          ) : (
            <AccountCard
              key={getShellExpandKey(shell)}
              assetTypeId={shell.assetTypeId}
              label={shell.label}
              isExpanded={expandedShellKey === getShellExpandKey(shell)}
              onToggle={() => handleToggle(getShellExpandKey(shell))}
              fundingSourceId={shell.fundingSourceId}
              existingSource={shell.selfFundingSource}
              onSaveComplete={() => handleSaveComplete(getShellExpandKey(shell))}
              disabled={disabled}
              allocationAmount={
                shell.fundingSourceId ? allocationsBySourceId[shell.fundingSourceId] : undefined
              }
              allocation={buildAllocationCallbacks(
                shell.fundingSourceId ?? null,
                shell.assetTypeId,
                shell.selfFundingSource,
              )}
            />
          ),
        )}

        {lockedShells.length > 0 && (
          <>
            <div className="pt-6">
              <h3 className="text-base font-semibold text-gray-950">
                Shared by {partnerFirstName ?? 'Partner'}
              </h3>
              <p className="text-foreground mt-1 text-sm">
                These accounts were shared from {partnerFirstName ?? 'your partner'}&apos;s plan and
                are included in your coverage assessment.
              </p>
            </div>
            {lockedShells.map(shell => (
              <AccountCard
                key={getShellExpandKey(shell)}
                assetTypeId={shell.assetTypeId}
                label={shell.label}
                isExpanded={expandedShellKey === getShellExpandKey(shell)}
                onToggle={() => handleToggle(getShellExpandKey(shell))}
                fundingSourceId={shell.fundingSourceId}
                existingSource={shell.selfFundingSource}
                onSaveComplete={() => handleSaveComplete(getShellExpandKey(shell))}
                disabled={disabled}
                lockedByPartnerName={partnerFirstName ?? undefined}
              />
            ))}
          </>
        )}
      </div>
      <AddPolicyModal
        open={existingLtcDrawerOpen}
        onClose={() => setExistingLtcDrawerOpen(false)}
        fundingSourceId={policyFundingSourceIdForDrawer}
        readOnly={isSessionComplete}
      />
    </>
  );
}
